const COLUMN_ALIASES = {
  phone: ['phone', 'contact_number', 'contact number', 'mobile', 'whatsapp', 'number'],
  customer_name: ['customer_name', 'customer name', 'name'],
  car: ['car', 'car_number', 'car number', 'vehicle_registration', 'vehicle number', 'vehicle'],
  car_model: ['car_model', 'car model', 'vehicle_model', 'model'],
  state: ['state'],
  pincode: ['pincode', 'pin_code', 'pin code'],
  membership_status: ['membership_status', 'membership status', 'status'],
  expiry_date: ['expiry_date', 'expiry date', 'expires_at', 'validity'],
  membership_number: ['membership_number', 'membership number', 'membership_card_number', 'card_number', 'rsa_number'],
  plan_name: ['plan_name', 'plan name', 'membership_plan', 'plan'],
  start_date: ['start_date', 'start date', 'membership_start', 'started_at'],
  email: ['email', 'email_id', 'email id'],
};

export const BULK_CSV_SAMPLE = `customer_name,phone,car,car_model,state,pincode,membership_number,plan_name,start_date,expiry_date
Rajesh Kumar,9876543210,DL01AB1234,Maruti Swift,Delhi,110001,RS1234567890,RSA Gold,01 Jan 2025,01 Jan 2026
Priya Sharma,9123456789,MH12CD5678,Hyundai i20,Maharashtra,411001,RS0987654321,RSA Silver,15 Mar 2025,15 Mar 2026`;

export const BULK_VARIABLES = [
  { key: 'customer_name', label: 'Customer Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'membership_number', label: 'Membership No.' },
  { key: 'plan_name', label: 'Plan Name' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'expiry_date', label: 'Expiry Date' },
  { key: 'membership_status', label: 'Membership Status' },
  { key: 'car', label: 'Car Number' },
  { key: 'car_model', label: 'Car Model' },
  { key: 'state', label: 'State' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'email', label: 'Email' },
];

function sanitizeCell(val) {
  const v = String(val ?? '').trim();
  return v.replace(/^"+|"+$/g, '').trim();
}

function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function normalizeHeader(header) {
  return sanitizeCell(header).toLowerCase().replace(/\*$/, '').trim();
}

function resolveCanonicalKey(header) {
  const normalized = normalizeHeader(header);
  const found = Object.entries(COLUMN_ALIASES).find(([, aliases]) =>
    aliases.some((alias) => alias === normalized),
  );
  return found ? found[0] : null;
}

export function normalizeBulkPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return digits;
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  return digits;
}

/** Suggest {{var}} tokens from template body context (Meta {{1}}, {{2}} order). */
export function suggestTemplateParams(template) {
  const n = Number(template?.param_count) || 0;
  if (!n) return [];
  const body = String(template?.body_text || '');
  const parts = body.split(/\{\{\d+\}\}/);
  const defaults = ['{{customer_name}}', '{{car}}', '{{expiry_date}}', '{{plan_name}}', '{{membership_number}}'];
  const pick = (context, index) => {
    const c = context.toLowerCase();
    if (/membership\s*no|member\s*id|card\s*no|card\s*number/.test(c)) return '{{membership_number}}';
    if (/start\s*date|valid\s*from|from\s*date|started/.test(c)) return '{{start_date}}';
    if (/expir|valid\s*till|valid\s*until|end\s*date/.test(c)) return '{{expiry_date}}';
    if (/plan|package|tier/.test(c)) return '{{plan_name}}';
    if (/customer|name|dear|hello/.test(c)) return '{{customer_name}}';
    if (/model/.test(c)) return '{{car_model}}';
    if (/car|vehicle|registration|reg\.?\s*no/.test(c)) return '{{car}}';
    if (/phone|mobile|contact|whatsapp/.test(c)) return '{{phone}}';
    if (/pin\s*code|pincode|zip/.test(c)) return '{{pincode}}';
    if (/state/.test(c)) return '{{state}}';
    if (/email/.test(c)) return '{{email}}';
    if (/status/.test(c)) return '{{membership_status}}';
    // Never leave empty — Meta #132012 rejects blank body params
    return defaults[index] || '{{customer_name}}';
  };
  return Array.from({ length: n }, (_, i) => pick(parts[i] || '', i));
}

export function recipientToVariables(row) {
  return {
    customer_name: row.customer_name || 'Customer',
    phone: row.phone || '',
    membership_number: row.membership_number || '',
    plan_name: row.plan_name || '',
    start_date: row.start_date || '',
    expiry_date: row.expiry_date || '',
    membership_status: row.membership_status || '',
    car: row.car || '',
    car_model: row.car_model || '',
    state: row.state || '',
    pincode: row.pincode || '',
    email: row.email || '',
  };
}

export function parseBulkMessageCsv(csvText) {
  const lines = String(csvText || '').replace(/^\uFEFF/, '').split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    throw new Error('CSV must include header row and at least one data row');
  }

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  const canonicalIndexes = headers.map((h) => resolveCanonicalKey(h));

  if (!canonicalIndexes.includes('phone')) {
    throw new Error('CSV must have a phone column (phone / contact_number / mobile)');
  }

  const rows = [];
  const errors = [];

  for (let i = 1; i < lines.length; i += 1) {
    const raw = lines[i];
    if (!raw.trim()) continue;
    const values = parseCsvLine(raw).map(sanitizeCell);
    const get = (key) => {
      const idx = canonicalIndexes.indexOf(key);
      return idx >= 0 ? values[idx] || '' : '';
    };

    const phone = normalizeBulkPhone(get('phone'));
    if (!phone || phone.length !== 10) {
      errors.push(`Row ${i + 1}: invalid phone`);
      continue;
    }

    const row = {
      customer_name: get('customer_name') || 'Customer',
      phone,
      car: get('car'),
      car_model: get('car_model'),
      state: get('state'),
      pincode: get('pincode'),
      membership_number: get('membership_number'),
      plan_name: get('plan_name'),
      start_date: get('start_date'),
      membership_status: get('membership_status'),
      expiry_date: get('expiry_date'),
      email: get('email'),
      variables: {},
    };
    row.variables = recipientToVariables(row);
    rows.push(row);
  }

  if (!rows.length) {
    throw new Error(errors[0] || 'No valid rows found in CSV');
  }

  return { rows, errors };
}

export function downloadBulkCsvSample() {
  const blob = new Blob([BULK_CSV_SAMPLE], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bulk_whatsapp_sample.csv';
  a.click();
  URL.revokeObjectURL(url);
}
